import {
  CalculatorSnapshot,
  categorySumMt,
  hasCategoryBreakdown,
  normalizeCalculatorSnapshot,
  parseCalculatorSnapshot,
  scaleCategoriesToTotal,
  snapshotTotalsMatch
} from './calculatorSnapshot';
import { CALCULATOR_IFRAME_ID } from './calculatorIframeMessages';
import { isNextCalculatorEmbedActive } from './calculatorEmbedUrl';

const COOLCLIMATE_API =
  'https://coolclimate.berkeley.edu/calculators/household-staging/api.php';
const COOLCLIMATE_DEV_TOKEN = 'DPP6zZDkSQAlRKfQ-iWgdg';

let latestCalculatorInputs: { [key: string]: unknown } = {};

export const setLatestCalculatorInputs = (inputs: { [key: string]: unknown }) => {
  if (!inputs || typeof inputs !== 'object') {
    return;
  }
  const next: { [key: string]: unknown } = {};
  const existingKeys = Object.keys(latestCalculatorInputs);
  for (let i = 0; i < existingKeys.length; i++) {
    next[existingKeys[i]] = latestCalculatorInputs[existingKeys[i]];
  }
  const keys = Object.keys(inputs);
  for (let i = 0; i < keys.length; i++) {
    next[keys[i]] = inputs[keys[i]];
  }
  latestCalculatorInputs = next;
};

export const getLatestCalculatorInputs = (): { [key: string]: unknown } => latestCalculatorInputs;

const parseXmlResponse = (xmlText: string): { [key: string]: string } => {
  const result: { [key: string]: string } = {};
  if (typeof DOMParser === 'undefined') {
    return result;
  }
  try {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    const root = doc.documentElement;
    if (!root) {
      return result;
    }
    const children = root.childNodes;
    for (let i = 0; i < children.length; i++) {
      const node = children[i];
      if (node.nodeType === 1 && node.nodeName) {
        result[node.nodeName] = (node.textContent || '').trim();
      }
    }
  } catch (_err) {
    return result;
  }
  return result;
};

const snapshotFromResultMap = (results: { [key: string]: unknown }): CalculatorSnapshot | undefined => {
  const parsed = parseCalculatorSnapshot({
    user_footprint: {
      compute_footprint: results
    },
    compute_footprint: results,
    value: results.result_grand_total || results.resultGrandTotal
  });
  if (!hasCategoryBreakdown(parsed.categories) && !(parsed.footprintMt > 0)) {
    return undefined;
  }
  return parsed;
};

/**
 * Read the embedded calculator's localStorage (`redux` / `user_footprint`) when
 * same-origin access is available. Cross-origin iframes fail silently.
 */
export const extractSnapshotFromEmbeddedStorage = (
  containerId: string = 'cc__calculator'
): CalculatorSnapshot | undefined => {
  try {
    const container = document.getElementById(containerId);
    const iframe = (container && container.querySelector('iframe')) as HTMLIFrameElement | null
      || document.getElementById(CALCULATOR_IFRAME_ID) as HTMLIFrameElement | null;
    if (!iframe || !iframe.contentWindow) {
      return undefined;
    }
    const storage = iframe.contentWindow.localStorage;
    if (!storage) {
      return undefined;
    }

    let compute: { [key: string]: unknown } | undefined;
    let inputs: { [key: string]: unknown } | undefined;

    try {
      const reduxRaw = storage.getItem('redux');
      if (reduxRaw) {
        const redux = JSON.parse(reduxRaw);
        const userFootprint = (redux && (redux.user_footprint || redux.userFootprint)) || {};
        compute = userFootprint.compute_footprint
          || userFootprint.computeFootprint
          || userFootprint.compute_takeaction_results;
        inputs = userFootprint.inputs;
        if (inputs) {
          setLatestCalculatorInputs(inputs);
        }
      }
    } catch (_err) {
      // ignore parse / access errors
    }

    if (!compute || !Object.keys(compute).length) {
      try {
        const footprintRaw = storage.getItem('user_footprint');
        if (footprintRaw) {
          const footprint = JSON.parse(footprintRaw);
          if (footprint && typeof footprint === 'object') {
            setLatestCalculatorInputs(footprint);
          }
        }
      } catch (_err) {
        // ignore
      }
    }

    if (compute && Object.keys(compute).length) {
      return snapshotFromResultMap(compute as { [key: string]: unknown });
    }
  } catch (_err) {
    // Cross-origin access throws; fall through to API / parent-cached inputs.
  }
  return undefined;
};

const stripResultFields = (source: { [key: string]: unknown }): { [key: string]: unknown } => {
  const next: { [key: string]: unknown } = {};
  const keys = Object.keys(source || {});
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (key === 'failed' || key.indexOf('result_') === 0) {
      continue;
    }
    next[key] = source[key];
  }
  return next;
};

/** Drop blank values so they cannot clobber defaults and then vanish from the POST body. */
const stripEmptyFields = (source: { [key: string]: unknown }): { [key: string]: unknown } => {
  const next: { [key: string]: unknown } = {};
  const keys = Object.keys(source || {});
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = source[key];
    if (value === undefined || value === null || value === '') {
      continue;
    }
    next[key] = value;
  }
  return next;
};

let cachedDefaultInputs: { [key: string]: unknown } | undefined;
let cachedDefaultInputsKey = '';

const coreDefaultsCacheKey = (inputs: { [key: string]: unknown }): string => {
  const parts = [
    String(inputs.input_location_mode || ''),
    String(inputs.input_location || ''),
    String(inputs.input_location_country || ''),
    String(inputs.input_income || ''),
    String(inputs.input_size || '')
  ];
  return parts.join('|');
};

const buildComputePayload = async (
  inputs: { [key: string]: unknown }
): Promise<{ [key: string]: unknown } | undefined> => {
  const observed = stripEmptyFields(stripResultFields(inputs || {}));
  if (!Object.keys(observed).length) {
    return undefined;
  }
  // Calculator UI always starts from location/size/income defaults, then overlays
  // user edits. Replaying only the sparse observed keys makes compute_footprint
  // fail (missing required params). Empty strings in observed used to override
  // good defaults and then get omitted from the POST body — same failure mode.
  const cacheKey = coreDefaultsCacheKey(observed);
  let defaultsOnly: { [key: string]: unknown } = {};
  if (cachedDefaultInputs && cachedDefaultInputsKey === cacheKey) {
    defaultsOnly = cachedDefaultInputs;
  } else {
    const defaultsXml = await fetchCalculatorDefaultInputs(observed);
    defaultsOnly = defaultsXml ? stripEmptyFields(stripResultFields(defaultsXml)) : {};
    if (Object.keys(defaultsOnly).length) {
      cachedDefaultInputs = defaultsOnly;
      cachedDefaultInputsKey = cacheKey;
    }
  }
  return { ...defaultsOnly, ...observed };
};

/**
 * Recompute category totals from the CoolClimate household API using the
 * latest inputs observed from the embedded calculator.
 */
export const fetchSnapshotFromCoolClimateApi = async (
  inputs?: { [key: string]: unknown }
): Promise<CalculatorSnapshot | undefined> => {
  if (isNextCalculatorEmbedActive()) {
    return undefined;
  }
  const payload = await buildComputePayload(inputs || getLatestCalculatorInputs());
  if (!payload) {
    return undefined;
  }

  const keys = Object.keys(payload);
  const bodyParts: string[] = [];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = payload[key];
    if (value === undefined || value === null || value === '') {
      continue;
    }
    bodyParts.push(
      encodeURIComponent(key) + '=' + encodeURIComponent(String(value))
    );
  }
  if (!bodyParts.length) {
    return undefined;
  }

  try {
    const response = await fetch(
      COOLCLIMATE_API + '?op=compute_footprint',
      {
        method: 'POST',
        headers: {
          'Accept': 'application/xml',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-DEV-TOKEN': COOLCLIMATE_DEV_TOKEN
        },
        body: bodyParts.join('&')
      }
    );
    if (!response.ok) {
      return undefined;
    }
    const xmlText = await response.text();
    const parsedXml = parseXmlResponse(xmlText);
    if (parsedXml.failed === '1' || !parsedXml.result_grand_total) {
      // Do not fall back to defaults-and-results for sharing — that returns the
      // unmodified baseline footprint and causes modal totals to disagree with
      // the live calculator (e.g. 43.6 vs the results-page chart).
      return undefined;
    }
    return snapshotFromResultMap(parsedXml);
  } catch (_err) {
    return undefined;
  }
};

/**
 * Fetch location/income/size-based default inputs from the CoolClimate
 * household API (`op=get_defaults_and_results`). These are the baseline values
 * progress should compare against — not previously saved user answers.
 */
export const fetchCalculatorDefaultInputs = async (
  inputs: { [key: string]: unknown }
): Promise<{ [key: string]: unknown } | undefined> => {
  if (isNextCalculatorEmbedActive()) {
    return undefined;
  }
  const params: string[] = ['op=get_defaults_and_results'];
  const coreKeys = [
    'input_location_mode',
    'input_location',
    'input_location_country',
    'input_income',
    'input_size'
  ];
  let hasCore = false;
  for (let i = 0; i < coreKeys.length; i++) {
    const key = coreKeys[i];
    if (inputs[key] !== undefined && inputs[key] !== null && inputs[key] !== '') {
      params.push(encodeURIComponent(key) + '=' + encodeURIComponent(String(inputs[key])));
      if (key === 'input_location' || key === 'input_location_country') {
        hasCore = true;
      }
    }
  }
  if (!hasCore) {
    return undefined;
  }
  try {
    const response = await fetch(COOLCLIMATE_API + '?' + params.join('&'), {
      method: 'GET',
      headers: {
        'Accept': 'application/xml',
        'X-DEV-TOKEN': COOLCLIMATE_DEV_TOKEN
      }
    });
    if (!response.ok) {
      return undefined;
    }
    const xmlText = await response.text();
    const parsedXml = parseXmlResponse(xmlText);
    if (!Object.keys(parsedXml).length) {
      return undefined;
    }
    const cleaned = stripEmptyFields(parsedXml);
    const cacheKey = coreDefaultsCacheKey(inputs);
    if (cacheKey && cacheKey !== '||||') {
      cachedDefaultInputs = stripResultFields(cleaned);
      cachedDefaultInputsKey = cacheKey;
    }
    return cleaned;
  } catch (_err) {
    return undefined;
  }
};

export const mergeCalculatorSnapshots = (
  base: CalculatorSnapshot | undefined,
  incoming: CalculatorSnapshot | undefined
): CalculatorSnapshot => {
  const safeBase = normalizeCalculatorSnapshot(base);
  if (!incoming) {
    return safeBase;
  }
  const safeIncoming = normalizeCalculatorSnapshot(incoming);
  return {
    footprintMt: safeIncoming.footprintMt > 0 ? safeIncoming.footprintMt : safeBase.footprintMt,
    similarHouseholdAverageMt: safeIncoming.similarHouseholdAverageMt || safeBase.similarHouseholdAverageMt,
    percentBelowSimilar: safeIncoming.percentBelowSimilar || safeBase.percentBelowSimilar,
    householdSize: safeIncoming.householdSize !== undefined
      ? safeIncoming.householdSize
      : safeBase.householdSize,
    categories: hasCategoryBreakdown(safeIncoming.categories)
      ? safeIncoming.categories
      : safeBase.categories
  };
};

/**
 * Prefer a CoolClimate API recompute from (defaults ⊕ live inputs). Category
 * bars are always scaled to the live headline total so the share modal never
 * disagrees with the results page (and never stays stuck on "Loading chart...").
 */
export const resolveAccurateCalculatorSnapshot = async (
  existing?: CalculatorSnapshot,
  containerId: string = 'cc__calculator'
): Promise<CalculatorSnapshot> => {
  let snapshot = normalizeCalculatorSnapshot(existing);
  const liveFootprint = snapshot.footprintMt;

  const candidateFootprint = (candidate?: CalculatorSnapshot): number => {
    if (!candidate) {
      return 0;
    }
    if (candidate.footprintMt > 0) {
      return candidate.footprintMt;
    }
    return categorySumMt(candidate.categories);
  };

  const closeness = (candidate?: CalculatorSnapshot): number => {
    const fp = candidateFootprint(candidate);
    if (!(fp > 0)) {
      return Number.POSITIVE_INFINITY;
    }
    if (!(liveFootprint > 0)) {
      return 0;
    }
    return Math.abs(fp - liveFootprint);
  };

  let best: CalculatorSnapshot | undefined;
  let bestDelta = Number.POSITIVE_INFINITY;

  const consider = (candidate?: CalculatorSnapshot) => {
    if (!candidate || !hasCategoryBreakdown(candidate.categories)) {
      return;
    }
    const delta = closeness(candidate);
    if (delta < bestDelta) {
      best = candidate;
      bestDelta = delta;
    }
  };

  consider(extractSnapshotFromEmbeddedStorage(containerId));
  consider(await fetchSnapshotFromCoolClimateApi(getLatestCalculatorInputs()));

  if (best && hasCategoryBreakdown(best.categories)) {
    const target = liveFootprint > 0 ? liveFootprint : candidateFootprint(best);
    snapshot = {
      ...snapshot,
      ...best,
      footprintMt: target,
      categories: scaleCategoriesToTotal(best.categories, target),
      householdSize: best.householdSize !== undefined
        ? best.householdSize
        : snapshot.householdSize,
      percentBelowSimilar: best.percentBelowSimilar || snapshot.percentBelowSimilar,
      similarHouseholdAverageMt:
        best.similarHouseholdAverageMt || snapshot.similarHouseholdAverageMt
    };
  } else if (liveFootprint > 0 && hasCategoryBreakdown(snapshot.categories)
    && !snapshotTotalsMatch(snapshot, 1.25)) {
    snapshot = {
      ...snapshot,
      footprintMt: liveFootprint,
      categories: scaleCategoriesToTotal(snapshot.categories, liveFootprint)
    };
  } else if (liveFootprint > 0) {
    snapshot = { ...snapshot, footprintMt: liveFootprint };
  }

  return normalizeCalculatorSnapshot(snapshot);
};
